import { useInfiniteQuery } from '@tanstack/react-query';
import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { DRILL_DOWN_PAGE_SIZE } from '../../../modules/global-search/globalSearch.constants';
import {
  GlobalSearchParams,
  GlobalSearchResponse,
  SearchableEntityType,
} from '../../../modules/global-search/globalSearch.types';
import { getGlobalSearchDrillDownKey, getGlobalSearchKey } from './globalSearch.keys';
import { findGlobalSearch } from './globalSearch.repository';

const STALE_TIME_MS = 30_000;

export const useGlobalSearchQuery = (params: GlobalSearchParams, options?: UseDataQueryOptions) => {
  const { brandId } = useApplicationContext();

  return useDataQuery<GlobalSearchResponse>(
    {
      queryKey: getGlobalSearchKey(params, brandId),
      queryFn: async (context) => {
        const response = await findGlobalSearch(params, context?.signal);
        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('failed to fetch global search results');
        }
        return response.data;
      },
      staleTime: STALE_TIME_MS,
      keepPreviousData: true,
    },
    options,
  );
};

interface UseGlobalSearchDrillDownParams {
  q: string;
  entityType: SearchableEntityType;
  startSkip: number;
}

interface UseGlobalSearchDrillDownOptions {
  enabled?: boolean;
}

export const useGlobalSearchDrillDown = (
  params: UseGlobalSearchDrillDownParams,
  options?: UseGlobalSearchDrillDownOptions,
) => {
  const { team, brandId } = useApplicationContext();

  return useInfiniteQuery({
    queryKey: [team?.id, ...getGlobalSearchDrillDownKey(params, brandId)],
    enabled: options?.enabled,
    staleTime: STALE_TIME_MS,
    initialPageParam: params.startSkip,
    queryFn: async ({ pageParam, signal }) => {
      const response = await findGlobalSearch(
        {
          q: params.q,
          entityTypes: [params.entityType],
          skip: pageParam,
          limit: DRILL_DOWN_PAGE_SIZE,
        },
        signal,
      );

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error('failed to fetch global search results');
      }

      return response.data;
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const group = lastPage.groups.find((item) => item.entityType === params.entityType);
      return group?.hasMore ? lastPageParam + DRILL_DOWN_PAGE_SIZE : undefined;
    },
  });
};
